import type {
  Base64Response,
  BatchArgs,
  BatchResponse,
  EnhancedPresentation,
  IActivity,
  IDemo,
  IOrganizationData,
  IPresentation,
  IVariable,
  Integration,
  PresentationTokenResponse,
} from '@carboncopyco/types';
import {
  Debug,
  encodeToBase64,
  removeUndefinedValues,
} from '@carboncopyco/utils';
import type { EndpointDefinitions } from '@reduxjs/toolkit/dist/query/react';
import type { QueryKeys } from '@reduxjs/toolkit/src/query/core/apiState';
import type { GetFiltersResponse } from '../../types';
import { handleBatchRequest } from '../utils';
import {
  queryDataUpdateHandler,
  workspaceRemoveAsset,
} from '../utils/queryDataUpdateHandler';
import { mainApi } from './mainApi';
import type { SearchResult } from './searchApi';

const debug = Debug.extend('demostack-data:api:presentationApi');

export interface RestorePresentationArgs {
  organizationId: string;
  id: string;
}

export interface DeletePresentationArgs {
  organizationId: string;
  id: string;
}

export interface UpdatePresentationArgs {
  organizationId: string;
  id: string;
  forClient?: string;
  image?: string;
  variables?: Record<string, any>;
}

export interface PresentationAnalyticsArgs {
  organizationId: string;
  id: string;
  revisionId: string;
}

export interface IntegrationMetadata {
  integrationId: Integration['id'];
  opportunityId: string;
}

// Backward compatibility - used to be an object of code-value pairs, and we would like to migrate for an array
// in order to support new types of variables, such as image variables
type PresentationVariables =
  | Record<string, any>
  | Pick<IVariable, 'variableId' | 'value'>[];

export type CreatePresentationArgs = {
  organizationId: string;
  workspaceId: string;
  demoId: string;
  forClient?: string;
  variables?: PresentationVariables;
  summary?: string;
  presentationName?: string;
  hidden?: boolean;
  notes?: string;
  integrationMetadata?: IntegrationMetadata;
  showGuidanceTooltip?: boolean;
};

export type CreatePresentationPreviewArgs = {
  organizationId: string;
  demoId: string;
  variables?: PresentationVariables;
};

export interface CreateTokenArgs {
  organizationId: string;
  presentationId: string;
}

export interface GetPresentationArgs {
  organizationId: string;
  id: string;
  removeDeleted?: boolean;
}

export interface GetPresentationsByDemoArgs extends BatchArgs {
  organizationId: string;
  workspaceId: string;
  demoId: string;
  deletedEntities?: boolean;
  companies?: string[];
  creators?: string[];
  clearCache?: boolean;
}

export interface GetPresentationsArgs extends BatchArgs {
  organizationId: string;
  workspaces?: string;
}

export interface GetPresentationsFiltersArgs {
  workspaces: string[];
  organizationId: IOrganizationData['id'];
  demoId: IDemo['id'];
  deletedEntities?: boolean;
  query: string;
}

export const presentationApi = mainApi.injectEndpoints({
  endpoints: (builder) => ({
    deletePresentation: builder.mutation<IPresentation, DeletePresentationArgs>(
      {
        query: ({ organizationId, id }) => ({
          url: `/api/${organizationId}/presentation/${id}`,
          method: 'DELETE',
        }),
        onQueryStarted: async (arg, { dispatch, getState, queryFulfilled }) => {
          try {
            const { data: restoredPresentation } = await queryFulfilled;
            const state = getState();
            queryDataUpdateHandler({
              state,
              dispatch,
              endpointName:
                'getPresentationsByDemo' as QueryKeys<EndpointDefinitions>,
              endpointArgumentKey: 'demoId',
              endpointArgumentValue: restoredPresentation.demoId?.toString(),
              updateQueryDataFunction: (archivedPresentationAssets) =>
                workspaceRemoveAsset(archivedPresentationAssets, arg),
            });
          } catch (error) {
            debug(error);
          }
        },
        invalidatesTags: (result, _, arg) =>
          result
            ? [
                { type: 'Presentation', id: arg.id },
                { type: 'Presentations' },
                { type: 'ArchivedPresentations' },
                {
                  type: 'DemoPresentations',
                  id: result.demoId,
                },
              ]
            : [
                { type: 'Presentation', id: arg.id },
                { type: 'Presentations' },
                { type: 'ArchivedPresentations' },
              ],
      }
    ),

    getPresentation: builder.query<IPresentation, GetPresentationArgs>({
      query: ({ organizationId, id, removeDeleted = false }) => ({
        url: `/api/${organizationId}/presentation/${id}`,
        method: 'GET',
        params: { removeDeleted: String(removeDeleted) },
      }),
      providesTags: ['Presentation'],
    }),

    updatePresentation: builder.mutation<IPresentation, UpdatePresentationArgs>(
      {
        query: ({ organizationId, id, forClient, image, variables }) => ({
          url: `/api/${organizationId}/presentation/${id}`,
          method: 'POST',
          body: {
            forClient,
            image,
            variables,
          },
        }),
        invalidatesTags: (result, _, { id }) =>
          result
            ? [
                { type: 'Presentation', id },
                { type: 'Presentations' },
                {
                  type: 'DemoPresentations',
                  id: result.demoId,
                },
              ]
            : [{ type: 'Presentation', id }, { type: 'Presentations' }],
      }
    ),

    restorePresentation: builder.mutation<
      IPresentation,
      RestorePresentationArgs
    >({
      query: ({ organizationId, id }) => ({
        url: `/api/${organizationId}/presentation/${id}/restore`,
        method: 'POST',
      }),
      onQueryStarted: async (arg, { dispatch, getState, queryFulfilled }) => {
        try {
          const { data: restoredPresentation } = await queryFulfilled;
          const state = getState();
          queryDataUpdateHandler({
            state,
            dispatch,
            endpointName:
              'getPresentationsByDemo' as QueryKeys<EndpointDefinitions>,
            endpointArgumentKey: 'demoId',
            endpointArgumentValue: restoredPresentation.demoId?.toString(),
            updateQueryDataFunction: (archivedPresentationAssets) =>
              workspaceRemoveAsset(archivedPresentationAssets, arg),
          });
        } catch (error) {
          debug('Failed to update the cache', error);
        }
      },
      invalidatesTags: (result) =>
        result
          ? [
              { type: 'ArchivedPresentations' },
              { type: 'Presentations' },
              {
                type: 'DemoPresentations',
                id: result.demoId,
              },
            ]
          : [{ type: 'ArchivedPresentations' }, { type: 'Presentations' }],
    }),

    getPresentationAnalytics: builder.query<
      BatchResponse<IActivity>,
      PresentationAnalyticsArgs
    >({
      query: ({ organizationId, id, revisionId, ...rest }) =>
        handleBatchRequest({
          ...rest,
          url: `/api/${organizationId}/presentation/${id}/revision/${revisionId}/analytics`,
        }),
      providesTags: (_, __, { id }) => [{ type: 'Presentation', id }],
    }),

    getPresentations: builder.query<
      BatchResponse<IPresentation>,
      GetPresentationsArgs
    >({
      query: ({ organizationId, workspaces = 'all', ...rest }) =>
        handleBatchRequest({
          ...rest,
          params: removeUndefinedValues({ workspaces }),
          url: `/api/${organizationId}/presentations`,
        }),
      providesTags: ['Presentations'],
    }),

    getArchivedPresentations: builder.query<
      BatchResponse<IPresentation>,
      GetPresentationsArgs
    >({
      query: ({ organizationId, workspaces = 'all', ...rest }) =>
        handleBatchRequest({
          ...rest,
          params: removeUndefinedValues({ workspaces }),
          url: `/api/${organizationId}/presentations/archived`,
        }),
      providesTags: ['ArchivedPresentations'],
    }),

    createPresentation: builder.mutation<IPresentation, CreatePresentationArgs>(
      {
        query: ({
          organizationId,
          workspaceId,
          demoId,
          forClient,
          variables,
          summary,
          presentationName,
          hidden,
          notes,
          integrationMetadata,
        }) => ({
          url: `/api/${organizationId}/presentations/new`,
          method: 'PUT',
          body: removeUndefinedValues({
            integrationMetadata,
            demoId,
            workspaceId,
            forClient,
            summary,
            presentationName,
            hidden,
            notes,
            variables,
          }),
          noFormatBody: Array.isArray(variables)
            ? undefined
            : {
                variables,
              },
        }),
        invalidatesTags: (res) =>
          res
            ? [
                { type: 'Presentations' },
                { type: 'DemoPresentations' },
                {
                  type: res.folderId ? 'FolderAssets' : 'WorkspaceAssets',
                  id: res.folderId || res.workspaceId,
                },
              ]
            : [{ type: 'Presentations' }, { type: 'DemoPresentations' }],
      }
    ),

    createPresentationPreview: builder.mutation<
      IPresentation,
      CreatePresentationPreviewArgs
    >({
      query: ({ organizationId, demoId, variables }) => ({
        url: `/api/${organizationId}/presentations/preview`,
        method: 'POST',
        body: removeUndefinedValues({
          demoId,
          variables,
        }),
        noFormatBody: Array.isArray(variables)
          ? undefined
          : {
              variables,
            },
      }),
    }),

    getPresentationsByDemo: builder.query<
      BatchResponse<EnhancedPresentation>,
      GetPresentationsByDemoArgs
    >({
      query: ({
        organizationId,
        demoId,
        deletedEntities,
        companies,
        creators,
        workspaceId,
        ...rest
      }) => {
        return handleBatchRequest({
          ...rest,
          params: removeUndefinedValues({
            workspaces: workspaceId,
            deletedEntities,
            companies: companies?.join(','),
            creators: creators?.join(','),
          }),
          url: `/api/${organizationId}/${demoId}/presentations`,
        });
      },
      onQueryStarted: async (arg, { dispatch, getState, queryFulfilled }) => {
        if (arg.clearCache) {
          try {
            const { data: presentations } = await queryFulfilled;
            const state = getState();
            queryDataUpdateHandler({
              state,
              dispatch,
              endpointName:
                'getPresentationsByDemo' as QueryKeys<EndpointDefinitions>,
              endpointArgumentKey: 'demoId',
              endpointArgumentValue: arg.demoId?.toString(),
              updateQueryDataFunction: (presentationAssets) => {
                return {
                  ...presentationAssets,
                  metadata: presentations.metadata,
                  items: presentations.items as unknown as SearchResult[],
                };
              },
            });
          } catch (error) {
            debug('Failed to update the cache', error);
          }
        }
      },
      providesTags: (_, __, { demoId, deletedEntities }) =>
        deletedEntities
          ? [{ type: 'ArchivedPresentations', id: demoId }]
          : [{ type: 'DemoPresentations', id: demoId }],
      serializeQueryArgs: ({ queryArgs }) => {
        if (!queryArgs) {
          return queryArgs;
        }
        const { nextBatchCursor, ...rest } = queryArgs;
        return rest;
      },
      merge: (queryCache, newBatch) => {
        queryCache.metadata = newBatch.metadata;
        const currentItemsIds = queryCache.items.map((item) => item.id);

        const newItems = newBatch.items.filter(
          (item) => !currentItemsIds.includes(item.id)
        );

        queryCache.items = [...queryCache.items, ...newItems];
      },
      forceRefetch({ currentArg, previousArg, endpointState }) {
        return (
          !(endpointState?.status === 'fulfilled') ||
          currentArg.nextBatchCursor !== previousArg.nextBatchCursor
        );
      },
    }),

    getPresentationsFilters: builder.query<
      GetFiltersResponse,
      GetPresentationsFiltersArgs
    >({
      query: ({ organizationId, demoId, deletedEntities, query }) => ({
        url: `/api/${organizationId}/${demoId}/presentations/filters`,
        method: 'GET',
        params: { deletedEntities, query },
      }),
    }),

    createPresentationToken: builder.mutation<
      Base64Response<PresentationTokenResponse>,
      CreateTokenArgs
    >({
      query: ({ organizationId, presentationId }) => ({
        url: `/api/${organizationId}/presentation/${presentationId}/token`,
        method: 'POST',
      }),
      transformResponse: (response: PresentationTokenResponse) => {
        return {
          value: encodeToBase64(response),
          baseObject: response,
        };
      },
    }),
  }),
});

export const {
  useDeletePresentationMutation,
  useGetPresentationQuery,
  useLazyGetPresentationQuery,
  useGetPresentationsQuery,
  useLazyGetPresentationsQuery,
  useCreatePresentationPreviewMutation,
  useUpdatePresentationMutation,
  useRestorePresentationMutation,
  useGetPresentationAnalyticsQuery,
  useLazyGetPresentationAnalyticsQuery,
  useGetArchivedPresentationsQuery,
  useLazyGetArchivedPresentationsQuery,
  useGetPresentationsByDemoQuery,
  useLazyGetPresentationsByDemoQuery,
  useCreatePresentationTokenMutation,
  useGetPresentationsFiltersQuery,
  useLazyGetPresentationsFiltersQuery,
} = presentationApi;
